import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { CONFIG } from '@config';
import type { Observable } from 'rxjs';

import type { OktaIntrospectResponse, OktaTokenResponse } from '../models/auth.models';
import { isTokenExpired } from '../utils/jwt.util';

import { CookieService } from './cookie.service';

const TOKEN_COOKIE_NAME = 'ic_access_token';

@Injectable()
export class TokenService {
  private readonly http = inject(HttpClient);
  private readonly cookieService = inject(CookieService);

  exchangeCodeForToken(authCode: string, codeVerifier: string): Observable<OktaTokenResponse> {
    const body = new HttpParams()
      .set('grant_type', 'authorization_code')
      .set('code', authCode)
      .set('redirect_uri', CONFIG.auth.oktaRedirectUri)
      .set('client_id', CONFIG.auth.oktaClientId)
      .set('code_verifier', codeVerifier);

    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });

    return this.http.post<OktaTokenResponse>(CONFIG.auth.oktaTokenUrl, body.toString(), { headers });
  }

  introspectToken(token: string): Observable<OktaIntrospectResponse> {
    const body = new HttpParams()
      .set('token', token)
      .set('client_id', CONFIG.auth.oktaClientId)
      .set('token_type_hint', 'access_token');

    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });

    return this.http.post<OktaIntrospectResponse>(CONFIG.auth.oktaIntrospectUrl, body.toString(), { headers });
  }

  storeToken(token: string): void {
    this.cookieService.set(TOKEN_COOKIE_NAME, token);
  }

  getToken(): string | null {
    return this.cookieService.get(TOKEN_COOKIE_NAME);
  }

  clearToken(): void {
    this.cookieService.delete(TOKEN_COOKIE_NAME);
  }

  isAuthenticated(): boolean {
    const token = this.getToken();

    if (!token) {
      return false;
    }

    return !isTokenExpired(token);
  }
}
